import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Message, Conversation, SendMessageData } from '@/types/message'
import { messageApi } from '@/services/api'
import { showSnackbar } from './uiSlice'

interface MessagesState {
    conversations: Conversation[]
    currentConversation: Conversation | null
    messages: Message[]
    isLoading: boolean
    error: string | null
    unreadCount: number
    hasMoreMessages: boolean
    isConnected: boolean
    typingUsers: string[]
    onlineUsers: string[]
}

const initialState: MessagesState = {
    conversations: [],
    currentConversation: null,
    messages: [],
    isLoading: false,
    error: null,
    unreadCount: 0,
    hasMoreMessages: true,
    isConnected: false,
    typingUsers: [],
    onlineUsers: [],
}

// Async thunks
export const fetchConversations = createAsyncThunk(
    'messages/fetchConversations',
    async (params: { page?: number; limit?: number }, { rejectWithValue }) => {
        try {
            const response = await messageApi.getConversations(params)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch conversations'
            return rejectWithValue(message)
        }
    }
)

export const fetchConversationById = createAsyncThunk(
    'messages/fetchConversationById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await messageApi.getConversationById(id)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.conversation
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch conversation'
            return rejectWithValue(message)
        }
    }
)

export const fetchMessages = createAsyncThunk(
    'messages/fetchMessages',
    async (params: { conversationId: string; page?: number; limit?: number }, { rejectWithValue }) => {
        try {
            const response = await messageApi.getMessages(params)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch messages'
            return rejectWithValue(message)
        }
    }
)

export const sendMessage = createAsyncThunk(
    'messages/sendMessage',
    async (data: SendMessageData, { rejectWithValue, dispatch }) => {
        try {
            const response = await messageApi.sendMessage(data)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.message
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to send message'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const createConversation = createAsyncThunk(
    'messages/createConversation',
    async (data: { receiverId: string; gigId?: string; initialMessage?: string }, { rejectWithValue, dispatch }) => {
        try {
            // Pass data directly - backend expects receiverId, not participantId
            const response = await messageApi.createConversation(data)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.conversation
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to create conversation'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const markMessagesAsRead = createAsyncThunk(
    'messages/markAsRead',
    async (conversationId: string, { rejectWithValue }) => {
        try {
            const response = await messageApi.markMessagesAsRead(conversationId)
            // @ts-ignore - Handle nested response: { success: true, data: {
            return { conversationId, count: response.data.data.count }
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to mark messages as read'
            return rejectWithValue(message)
        }
    }
)

export const deleteMessage = createAsyncThunk(
    'messages/deleteMessage',
    async (messageId: string, { rejectWithValue, dispatch }) => {
        try {
            await messageApi.deleteMessage(messageId)
            return messageId
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to delete message'
            dispatch(showSnackbar({ message, type: 'error' }))
            return rejectWithValue(message)
        }
    }
)

export const fetchUnreadCount = createAsyncThunk(
    'messages/fetchUnreadCount',
    async (_, { rejectWithValue }) => {
        try {
            const response = await messageApi.getUnreadCount()
            // @ts-ignore - Handle nested response: { success: true, data: {
            return response.data.data.count
        } catch (error: any) {
            const message = error.response?.data?.error?.message || 'Failed to fetch unread count'
            return rejectWithValue(message)
        }
    }
)



const messagesSlice = createSlice({
    name: 'messages',
    initialState,
    reducers: {
        clearCurrentConversation: (state) => {
            state.currentConversation = null
            state.messages = []
            state.hasMoreMessages = true
        },
        setCurrentConversation: (state, action: PayloadAction<Conversation>) => {
            state.currentConversation = action.payload
        },
        clearError: (state) => {
            state.error = null
        },
        addMessage: (state, action: PayloadAction<Message>) => {
            const message = action.payload
            console.log('Redux addMessage called:', {
                messageId: message._id,
                messageConversation: message.conversation,
                currentConversationId: state.currentConversation?._id,
                messagesLength: state.messages.length
            })

            // Convert IDs to strings to ensure proper comparison
            const messageConversationId = message.conversation
            const currentConversationId = state.currentConversation?._id?.toString()

            // Add message to messages array if it's for current conversation
            if (currentConversationId === messageConversationId) {
                const existingIndex = state.messages.findIndex(m => m._id === message._id)
                if (existingIndex === -1) {
                    state.messages.push(message)
                    console.log('Message added to current conversation messages array')
                } else {
                    console.log('Message already exists in messages array')
                }
            } else {
                console.log('Message not for current conversation:', {
                    currentConversationId,
                    messageConversationId
                })
            }

            // Update conversation's last message
            const conversationIndex = state.conversations.findIndex(c => c._id === messageConversationId)
            if (conversationIndex !== -1) {
                state.conversations[conversationIndex].lastMessage = message
                state.conversations[conversationIndex].updatedAt = message.createdAt

                // Move conversation to top
                const conversation = state.conversations.splice(conversationIndex, 1)[0]
                state.conversations.unshift(conversation)

                // Update unread count if message is not from current user
                if (!message.isFromCurrentUser && messageConversationId !== currentConversationId) {
                    state.unreadCount += 1
                    state.conversations[0].unreadCount = (state.conversations[0].unreadCount || 0) + 1
                }

                // Update total count for any new message
                // @ts-ignore - totalCount property was removed from state
                state.totalCount += 1
            }
        },
        updateMessage: (state, action: PayloadAction<Message>) => {
            const updatedMessage = action.payload
            const messageIndex = state.messages.findIndex(m => m._id === updatedMessage._id)

            if (messageIndex !== -1) {
                state.messages[messageIndex] = updatedMessage
            }
        },
        removeMessage: (state, action: PayloadAction<string>) => {
            const messageId = action.payload
            state.messages = state.messages.filter(m => m._id !== messageId)
        },
        setConnectionStatus: (state, action: PayloadAction<boolean>) => {
            state.isConnected = action.payload
        },
        setTypingUsers: (state, action: PayloadAction<string[]>) => {
            state.typingUsers = action.payload
        },
        addTypingUser: (state, action: PayloadAction<string>) => {
            if (!state.typingUsers.includes(action.payload)) {
                state.typingUsers.push(action.payload)
            }
        },
        removeTypingUser: (state, action: PayloadAction<string>) => {
            state.typingUsers = state.typingUsers.filter(user => user !== action.payload)
        },
        setOnlineUsers: (state, action: PayloadAction<string[]>) => {
            state.onlineUsers = action.payload
        },
        addOnlineUser: (state, action: PayloadAction<string>) => {
            if (!state.onlineUsers.includes(action.payload)) {
                state.onlineUsers.push(action.payload)
            }
        },
        removeOnlineUser: (state, action: PayloadAction<string>) => {
            state.onlineUsers = state.onlineUsers.filter(user => user !== action.payload)
        },
        updateUnreadCount: (state, action: PayloadAction<number>) => {
            state.unreadCount = Math.max(0, state.unreadCount + action.payload)
        },
        resetUnreadCount: (state) => {
            state.unreadCount = 0
        },
        updateConversation: (state, action: PayloadAction<Conversation>) => {
            const updatedConversation = action.payload
            const index = state.conversations.findIndex(c => c._id === updatedConversation._id)

            if (index !== -1) {
                state.conversations[index] = updatedConversation
            }

            if (state.currentConversation?._id === updatedConversation._id) {
                state.currentConversation = updatedConversation
            }
        },
        prependMessages: (state, action: PayloadAction<Message[]>) => {
            const newMessages = action.payload
            state.messages = [...newMessages, ...state.messages]

            if (newMessages.length === 0) {
                state.hasMoreMessages = false
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch Conversations
        builder
            .addCase(fetchConversations.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchConversations.fulfilled, (state, action) => {
                state.isLoading = false
                state.conversations = action.payload.conversations
            })
            .addCase(fetchConversations.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Conversation by ID
        builder
            .addCase(fetchConversationById.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchConversationById.fulfilled, (state, action) => {
                state.isLoading = false
                state.currentConversation = action.payload
            })
            .addCase(fetchConversationById.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Messages
        builder
            .addCase(fetchMessages.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchMessages.fulfilled, (state, action) => {
                state.isLoading = false
                const { messages, hasMore, isInitialLoad } = action.payload

                if (isInitialLoad) {
                    state.messages = messages
                } else {
                    state.messages = [...messages, ...state.messages]
                }

                state.hasMoreMessages = hasMore
            })
            .addCase(fetchMessages.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Send Message
        builder
            .addCase(sendMessage.pending, (state) => {
                state.error = null
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                const message = action.payload

                // Always add message to current messages array since user just sent it
                const existingMessageIndex = state.messages.findIndex(m => m._id === message._id)
                if (existingMessageIndex === -1) {
                    state.messages.push(message)
                }

                // Update conversation
                const conversationIndex = state.conversations.findIndex(c => c._id === message.conversation)
                if (conversationIndex !== -1) {
                    state.conversations[conversationIndex].lastMessage = message
                    state.conversations[conversationIndex].updatedAt = message.createdAt

                    // Move to top
                    const conversation = state.conversations.splice(conversationIndex, 1)[0]
                    state.conversations.unshift(conversation)
                }

                // Update total count
                // @ts-ignore - totalCount property was removed from state
                state.totalCount += 1
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.error = action.payload as string
            })

        // Create Conversation
        builder
            .addCase(createConversation.fulfilled, (state, action) => {
                const conversation = action.payload
                const existingIndex = state.conversations.findIndex(c => c._id === conversation._id)

                if (existingIndex === -1) {
                    state.conversations.unshift(conversation)
                }

                state.currentConversation = conversation
            })

        // Mark Messages as Read
        builder
            .addCase(markMessagesAsRead.fulfilled, (state, action) => {
                const { conversationId, count } = action.payload

                // Update unread count
                state.unreadCount = Math.max(0, state.unreadCount - count)

                // Update conversation unread count
                const conversationIndex = state.conversations.findIndex(c => c._id === conversationId)
                if (conversationIndex !== -1) {
                    state.conversations[conversationIndex].unreadCount = 0
                }

                // Mark messages as read
                state.messages = state.messages.map(message =>
                    message.conversation === conversationId
                        ? { ...message, read: true }
                        : message
                )
            })

        // Delete Message
        builder
            .addCase(deleteMessage.fulfilled, (state, action) => {
                const messageId = action.payload

                // Remove the message from the messages array
                state.messages = state.messages.filter(m => m._id !== messageId)

                // Update total count
                // @ts-ignore - totalCount property was removed from state
                state.totalCount = Math.max(0, state.totalCount - 1)

                // The backend should handle updating the conversation's lastMessage
                // We'll refresh conversations to get the updated lastMessage from backend
                // For now, just remove from current messages list
            })

        // Fetch Unread Count
        builder
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload
            })
    },
})

export const {
    clearCurrentConversation,
    setCurrentConversation,
    clearError,
    addMessage,
    updateMessage,
    removeMessage,
    setConnectionStatus,
    setTypingUsers,
    addTypingUser,
    removeTypingUser,
    setOnlineUsers,
    addOnlineUser,
    removeOnlineUser,
    updateUnreadCount,
    resetUnreadCount,
    updateConversation,
    prependMessages,
} = messagesSlice.actions

export default messagesSlice.reducer